"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@plura/shared/utils";

import { Icon } from "@plura/client/components";

interface FAQItem {
  title: string;
  description: string;
}

interface AccordionProps {
  items: FAQItem[];
}

const Accordion = ({ items }: AccordionProps) => {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => {
        const isOpen = openItems.has(index);
        return (
          <div
            key={index}
            className="overflow-hidden rounded-xl border border-s-stroke2 dark:border-s-stroke2"
          >
            <button
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between border-none bg-b-surface2 px-6 py-4 text-left transition-colors duration-200",
                "hover:bg-b-depth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01 focus-visible:ring-offset-2 focus-visible:ring-offset-b-surface2",
                "dark:bg-b-dark1 dark:hover:bg-shade-04 dark:focus-visible:ring-offset-b-dark1",
                isOpen && "bg-b-surface1 outline-none dark:bg-shade-04",
              )}
              onClick={() => toggleItem(index)}
              aria-expanded={isOpen}
            >
              <h3 className="flex-1 pr-4 text-lg font-medium text-t-primary dark:text-t-light">
                {item.title}
              </h3>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="shrink-0"
                aria-hidden
              >
                <Icon name="chevron" className="!size-8 fill-t-secondary" />
              </motion.div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="border-t border-s-subtle bg-b-surface1 px-6 py-4 dark:border-s-stroke2 dark:bg-shade-04">
                    <p className="m-0 text-body-2 leading-relaxed text-t-primary dark:text-t-light">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
